import { headerAndFooter as config } from '@config';
import PageHeading from './page-heading';
import HeadingPropagator from './heading-propagator';

const headingLevels = 5;

export default class HeadingsListener {
  #headingSection;
  #headingLevel;

  #knownHeadings = [];
  #knownHeadingsByLevel = {};

  #canvas;
  #currentPageHeading;
  #headingPropagator;

  constructor () {
    this.#currentPageHeading = new PageHeading();
    this.#headingPropagator = new HeadingPropagator(this.#currentPageHeading);

    this.#addProjectLoadListeners();
    this.#addDesignModificationListeners();
  }

  #addProjectLoadListeners () {
    // This is a temporary fix, will fix in page-worker in the future
    INKIVE.EditorFacade.on('header-and-footer-saved', () => this.#headingPropagator.forceUpdateHeadingOnAllPages(this.#headingLevel));

    INKIVE.EditorFacade.on('page.move_to', () => {
      if (!this.#headingSection) return;

      this.#loadKnownHeadings();
      this.#loadCurrentPage();
    });

    INKIVE.EditorFacade.on('history.undo', () => {
      if (!this.#headingSection) return;
      this.#onPotentialHeadingSwap();
      this.#loadCurrentPage();
    });
  }

  #getHeadingSection () {
    const sectionId = config[this.#headingSection]?.id;
    if (!sectionId) return;

    return document.getElementById(sectionId);
  }

  setHeadingSection ({ id, level }) {
    const isHeadingSectionChanged = this.#headingSection !== id;

    if (!(id && level) || isHeadingSectionChanged) this.#currentPageHeading.removePageHeading();

    this.#headingSection = id;
    this.#headingLevel = level;

    this.#currentPageHeading.setLayoutSectionId(config[id]?.id);

    this.#loadKnownHeadings();
    this.#loadCurrentPage();

    if (!this.#knownHeadingsByLevel[this.#headingLevel]) return;

    const hasHeading = !!this.#knownHeadingsByLevel[this.#headingLevel].length;
    hasHeading &&
      this.#attemptUpwardsPropagation(this.#headingLevel, this.#knownHeadingsByLevel[this.#headingLevel][0]);
  }

  #loadCurrentPage () {
    const layoutSection = this.#getHeadingSection();

    this.#currentPageHeading.load(this.#headingLevel, layoutSection, true);
  }

  #addDesignModificationListeners () {
    INKIVE.DesignFacade.on('delete', id => {
      if (!this.#headingSection) return;
      this.#knownHeadings.includes(id) && this.#onPotentialHeadingSwap(id);
    });

    INKIVE.DesignFacade.on('text.create', ({ id, content }) => {
      if (!this.#headingSection) return;

      this.#loadKnownHeadings();
      !this.#currentPageHeading.hasPageHeadingElement() && this.#loadCurrentPage();
      this.#knownHeadings.includes(id) && this.#onPotentialHeadingSwap();

      typeof content !== 'undefined' &&
        this.#knownHeadings.includes(id) &&
        this.#onHeadingUpdate(id, content);
    });

    INKIVE.DesignFacade.on('text.update', async text => {
      if (!this.#headingSection) return;
      if (text) {
        typeof text.heading !== 'undefined' &&
          this.#onPotentialHeadingSwap(); // We don't want to pass the id here as more than one level may have changed

        const content = text.text_content || text.text_overflow;
        typeof content !== 'undefined' &&
          this.#knownHeadings.includes(text.id) &&
          this.#onHeadingUpdate(text.id, content);
      }
    });

    INKIVE.DesignFacade.on('move', async () => {
      if (!this.#headingSection) return;
      await new Promise(resolve => INKIVE.Helpers.afterRendering(resolve));

      this.#canvas = this.#canvas || INKIVE.Tool.getInstance().editor.canvas;

      for (const element of this.#canvas.getSelectedElems()) {
        if (this.#knownHeadings.includes(element.id)) {
          this.#onPotentialHeadingSwap(element.id);
          break;
        }
      }
    });

    INKIVE.DesignFacade.on('text.update.heading', text => {
      if (!this.#headingSection || !text?.id) return;

      this.#onPotentialHeadingSwap();
      this.#onHeadingUpdate(text.id, text.text_content || text.text_overflow);
    });
  }

  #onPotentialHeadingSwap (id) {
    const previousHeadingsByLevel = { ...this.#knownHeadingsByLevel };
    const updatedLevel = id && Object.keys(this.#knownHeadingsByLevel)
      .find(level => this.#knownHeadingsByLevel[level].includes(id));

    this.#loadKnownHeadings();

    if (updatedLevel) {
      this.#onPotentialHeadingLevelSwap(previousHeadingsByLevel, parseInt(updatedLevel));
    } else {
      for (let i = 1; i <= headingLevels; i++) {
        this.#onPotentialHeadingLevelSwap(previousHeadingsByLevel, i);
      }
    }
  }

  #onPotentialHeadingLevelSwap (previousHeadingsByLevel, level) {
    let isFirstDifferent;
    let isLastDifferent;

    const prev = previousHeadingsByLevel[level];
    const curr = this.#knownHeadingsByLevel[level];

    if ((!!prev.length) !== (!!curr.length)) {
      isFirstDifferent = isLastDifferent = true;
    } else {
      isFirstDifferent = curr[0] !== prev[0];
      isLastDifferent = curr[curr.length - 1] !== prev[prev.length - 1];
    }

    isFirstDifferent && this.#attemptUpwardsPropagation(level, curr[0]);
    isLastDifferent && this.#attemptDownwardsPropagation(level, curr.length ? curr[curr.length - 1] : null);
  }

  #onHeadingUpdate (id, content) {
    let level;
    let isFirst = false;
    let isLast = false;

    for (let i = 1; i <= headingLevels; i++) {
      const index = this.#knownHeadingsByLevel[i].indexOf(id);

      if (index !== -1) {
        isFirst = index === 0;
        isLast = index === this.#knownHeadingsByLevel[i].length - 1;
        level = i;

        isFirst && this.#headingPropagator.propagateIdUpwards(level, id, content);
        isLast && this.#headingPropagator.propagateIdDownwards(level, id, content);
      }
    }
  }

  #loadKnownHeadings () {
    const headings = this.#getPageHeadings();

    this.#knownHeadings = headings.map(e => e.id);

    for (let i = 1; i <= headingLevels; i++) {
      this.#knownHeadingsByLevel[i] = headings
        .filter(e => Number(e.heading) === i || e.text.strings.find(e => Number(e.heading) === i))
        .sort((a, b) => a.position?.y > b.position?.y ? 1 : -1)
        .map(e => e.id);
    }
  }

  #getPageHeadings () {
    const textElements = INKIVE.DesignFacade.get().filter(element => element.type === 'text');
    const textElementsWithHeadings = textElements.filter(element => this.#getTextElementsWithHeading(element));

    return textElementsWithHeadings;
  }

  #getTextElementsWithHeading (textElement) {
    return textElement.heading || textElement.text.strings.find(string => string.heading);
  }

  #attemptUpwardsPropagation (level, id) {
    if (id) {
      this.#headingPropagator.propagateFacadeDataUpwards(level, INKIVE.DesignFacade.get(id));
    } else {
      this.#headingPropagator.onUnknownHeading(level);
    }
  }

  #attemptDownwardsPropagation (level, id) {
    if (id) {
      const { content } = INKIVE.DesignFacade.get(id);
      this.#headingPropagator.propagateIdDownwards(level, id, content);
    } else {
      this.#headingPropagator.onUnknownHeading(level);
    }
  }

  propagateHeadingChanges () {
    this.#loadKnownHeadings();
    this.#loadCurrentPage();
    this.#onPotentialHeadingSwap();
  }

  propagatePageHeadingSettings () {
    this.#headingPropagator.propagatePageHeadingSettings();
  }
}
